import { useCallback, useContext, useMemo } from "react";

import { cloneDeep, partition } from "lodash";

import type { IChipDownContainerProps } from "@similarweb/ui-components";
import { ChipDownContainer, EllipsisDropdownItem } from "@similarweb/ui-components";

import { GraphTypeSwitcher } from "app/components/React/GraphTypeSwitcher/GraphTypeSwitcher";
import { i18nFilter } from "app/filters/i18nFilter";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import { TimeGranularitySwitcher } from "app/pages/website-analysis/TrafficAndEngagement/ChartUtilities/TimeGranularitySwitcher";
import type { IChannel } from "../Helpers/SearchOverviewGraphConfig";
import {
    ALL_CHANNELS,
    availableCategories,
    availableDataTypes,
    BRANDED_NON_BRANDED_CATEGORY,
    BRANDED_NON_BRANDED_CHANNELS,
    MMX_ORGANIC_PAID_CATEGORY,
    NUMBERS,
    ORGANIC_PAID,
    ORGANIC_PAID_CATEGORY,
    ORGANIC_PAID_CHANNELS,
    PERCENTS,
    timeGranularityList,
} from "../Helpers/SearchOverviewGraphConfig";
import { searchOverviewContext } from "../SearchOverviewGraphContext";
import type { ICategory } from "../SearchOverviewGraphReducer";
import { isCategoryAvailableForState, isDataTypeAvailableForState } from "../SearchOverviewGraphReducer";

import {
    ButtonsContainer,
    ChipDownContainer as ChipDownWrapper,
    FiltersRowContainer,
    FiltersRowStyle,
    FiltersTitle,
} from "./StyledComponents";
import { allTrackers } from "app/services/track/track";

interface IUseChannelsDropdown {
    category: ICategory;
    sourceConfig: IChannel[];

    getChipdownProps?(): Partial<IChipDownContainerProps>;
}

const ChannelsDropdown = ({ category, sourceConfig, getChipdownProps }: IUseChannelsDropdown) => {
    const {
        channel,
        category: selectedCategory,
        actions: { selectChannel, resetChannelAndCategory },
        selectedMetricTab,
    } = useContext(searchOverviewContext);
    const [[selectedItem], allTheRest] = useMemo(() => {
        return partition(sourceConfig, (item) => {
            return channel !== ALL_CHANNELS && category === selectedCategory && item.id === channel;
        });
    }, [channel, sourceConfig, selectedCategory, category]);
    const onClick = useCallback(
        ({ id, cat = category }) => {
            if (id === ALL_CHANNELS) {
                return resetChannelAndCategory();
            }
            if (category !== selectedCategory || id !== channel) {
                allTrackers.dispatchTrackEvent(
                    "Tab",
                    "click",
                    `Over Time Graph/Search Overview/${selectedMetricTab.name || ""}/${category.id || ""}`,
                );
                selectChannel({ channel: id, category: cat });
            }
        },
        [channel, category, selectedCategory],
    );

    const selectedText = selectedItem?.text;
    const buttonText = (selectedItem ?? sourceConfig[0])?.text;
    const userProps = getChipdownProps?.() ?? {};
    if (!userProps.disabled) {
        userProps.disabled = userProps.disabled || (channel !== ALL_CHANNELS && !selectedItem);
    }
    return (
        <ChipDownWrapper>
            <ChipDownContainer
                selectedIds={selectedItem ? { [selectedItem.id]: true } : {}}
                selectedText={i18nFilter()(selectedText)}
                onClick={onClick}
                onCloseItem={resetChannelAndCategory}
                buttonText={i18nFilter()(buttonText)}
                width={240}
                tooltipDisabled={true}
                noResultsMessage={i18nFilter()("dropdown.no.results")}
                {...userProps}
            >
                {allTheRest.map(({ id, text }) => (
                    <EllipsisDropdownItem
                        key={id}
                        id={id}
                    >
                        {i18nFilter()(text)}
                    </EllipsisDropdownItem>
                ))}
            </ChipDownContainer>
        </ChipDownWrapper>
    );
};

const OrganicPaidDropdown = () => {
    const state = useContext(searchOverviewContext);
    const getChipdownProps = () => ({
        disabled: !isCategoryAvailableForState(state, ORGANIC_PAID_CATEGORY),
    });
    return (
        <ChannelsDropdown
            category={ORGANIC_PAID_CATEGORY}
            sourceConfig={ORGANIC_PAID_CHANNELS}
            getChipdownProps={getChipdownProps}
        />
    );
};

const MMXOrganicPaidDropdown = () => {
    const state = useContext(searchOverviewContext);
    const getChipdownProps = () => ({
        disabled: !isCategoryAvailableForState(state, MMX_ORGANIC_PAID_CATEGORY),
    });
    return (
        <ChannelsDropdown
            category={MMX_ORGANIC_PAID_CATEGORY}
            sourceConfig={ORGANIC_PAID_CHANNELS}
            getChipdownProps={getChipdownProps}
        />
    );
};

const BrandedNonBrandedDropdown = () => {
    const state = useContext(searchOverviewContext);
    const getChipdownProps = () => ({
        disabled: !isCategoryAvailableForState(state, BRANDED_NON_BRANDED_CATEGORY),
    });
    return (
        <ChannelsDropdown
            category={BRANDED_NON_BRANDED_CATEGORY}
            sourceConfig={BRANDED_NON_BRANDED_CHANNELS}
            getChipdownProps={getChipdownProps}
        />
    );
};

export const useAvailableCategories = () => {
    const state = useContext(searchOverviewContext);
    const isCategoryAvailable = isCategoryAvailableForState(state);
    return availableCategories
        .map((category) => ({
            ...category,
            disabled: !isCategoryAvailable(category),
        }))
        .filter(({ disabled }) => !disabled);
};

const useAvailableDataTypes = () => {
    const state = useContext(searchOverviewContext);
    const isDataTypeAvailable = isDataTypeAvailableForState(state);
    return availableDataTypes
        .map((item) => ({
            ...item,
            disabled: !isDataTypeAvailable(item.value),
        }))
        .filter((item) => !item.disabled);
};

const CategoriesTabs = () => {
    const {
        category,
        selectedMetricTab,
        actions: { setCategory },
    } = useContext(searchOverviewContext);
    const availableCategories = useAvailableCategories();
    const selectedIndex = availableCategories.findIndex(({ id }) => category.id === id);
    const onItemClick = (index) => {
        const category = availableCategories[index];
        setCategory(category);
        allTrackers.dispatchTrackEvent(
            "Tab",
            "click",
            `Over Time Graph/Search Overview/${selectedMetricTab.name || ""}/${category.id || ""}`,
        );
    };

    return (
        availableCategories.length > 1 && (
            <SwitcherGranularityContainer
                key={selectedMetricTab.name}
                itemList={availableCategories}
                selectedIndex={selectedIndex}
                onItemClick={(index) => onItemClick(index)}
                customClass={"CircleSwitcher"}
            />
        )
    );
};

const DataTypesSwitcher = () => {
    const {
        dataType,
        actions: { setDataType },
        category,
        selectedMetricTab,
    } = useContext(searchOverviewContext);
    const availableDataTypes = useAvailableDataTypes();
    const onGraphTypeSwitcherClick = (index) => {
        // PERCENTS
        const type = availableDataTypes[index].value;
        if (type === PERCENTS) {
            allTrackers.dispatchTrackEvent(
                "Measure Button",
                "click",
                `Over Time Graph/Search Overview/${selectedMetricTab.name || ""}/${category.id || ""}/percentage`,
            );
        }
        if (type === NUMBERS) {
            allTrackers.dispatchTrackEvent(
                "website_analysis.marketing_channels.search_overview.graph_type.NUMBERS",
                "click",
                "website_analysis.marketing_channels.search_overview.graph_type.NUMBERS",
            );
        }

        const newDataType = { ...dataType };
        newDataType[selectedMetricTab.name][category.id] = type;
        setDataType(newDataType);
    };
    return (
        availableDataTypes.length > 1 && (
            <GraphTypeSwitcher
                onItemClick={(index) => onGraphTypeSwitcherClick(index)}
                selectedIndex={availableDataTypes.findIndex(
                    (item) => item.value === dataType[selectedMetricTab.name][category.id],
                )}
                buttonsList={availableDataTypes}
            />
        )
    );
};

const TimeGranularitySwitcherContainer = () => {
    const {
        actions: { setTimeGranularity },
        granularity,
        category,
    } = useContext(searchOverviewContext);
    const granularityUpdate = (granularityIndex) => {
        const newTimeGranularity = granularityIndexToGranularityObject(granularityIndex).name;
        setTimeGranularity(newTimeGranularity);
    };
    const granularityNameToGranularityObject = (granularityName) =>
        Object.values(timeGranularityList).find(({ name }) => name === granularityName);
    const granularityIndexToGranularityObject = (granularityIndex) =>
        Object.values(timeGranularityList).find(({ index }) => index === granularityIndex);

    const getTimeGranularity = () => {
        const timeGranularityOption = cloneDeep(timeGranularityList);
        if (category.id !== ORGANIC_PAID) {
            timeGranularityOption.daily.disabled = true;
            timeGranularityOption.weekly.disabled = true;
            if (String(granularity) !== timeGranularityOption.monthly.name) {
                setTimeGranularity(timeGranularityOption.monthly.name);
            }
        }
        const timeGranularityArray = Object.values(timeGranularityOption);
        return timeGranularityArray;
    };
    return (
        <TimeGranularitySwitcher
            timeGranularity={granularityNameToGranularityObject(granularity)}
            granularityUpdate={granularityUpdate}
            getGranularity={getTimeGranularity}
        />
    );
};

export const FiltersRow = () => {
    const context = useContext(searchOverviewContext);
    const { emptyState, isSingle, isTotal, isDesktop } = context;
    return (
        <FiltersRowContainer>
            <FiltersRowStyle data-automation="search-tab-graph-filters">
                <FiltersTitle>{i18nFilter()("search.overview.graph.filters.row.title")}</FiltersTitle>
                {!isSingle ? (
                    <>
                        {isDesktop ? <OrganicPaidDropdown /> : <MMXOrganicPaidDropdown />}
                        <BrandedNonBrandedDropdown />
                    </>
                ) : null}
                {isSingle ? <CategoriesTabs /> : null}
            </FiltersRowStyle>
            {!emptyState ? (
                <ButtonsContainer data-automation="search-tab-graph-butttons">
                    <DataTypesSwitcher />
                    <TimeGranularitySwitcherContainer />
                </ButtonsContainer>
            ) : null}
        </FiltersRowContainer>
    );
};
